import React, { useState } from 'react';
import { postWithCSRFToken } from './registrationUtils';
import styled from 'styled-components';

import { BrightInput } from 'components/forms/BrightInput';
import { ErrorMessage } from 'core/typography';
import { StandardLink } from 'components/links/StandardLink';
import { isGovcloud } from 'utils/environmentUtils';
import {
  xrystalDeepOcean,
  xrystalBrightAzure,
  xrystalViolet,
  xrystalDarkNavy,
  xrystalButtonStart,
  xrystalButtonEnd,
  white,
} from 'core/colors';

const StyledXrystalButton = styled.button<{ pending: boolean }>`
  width: 100%;
  padding: 1rem 2rem;
  border-radius: 0.5rem;
  font-weight: bold;
  color: ${white};
  font-size: 1.125rem;
  transition: all 0.2s ease-in-out;
  transform: scale(1);
  min-height: 56px;
  border: none;
  cursor: pointer;
  box-shadow: 0 6px 20px ${xrystalViolet}40;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  background: ${(props) =>
    props.pending
      ? `linear-gradient(135deg, ${xrystalButtonStart}90 0%, ${xrystalButtonEnd}90 100%)`
      : `linear-gradient(135deg, ${xrystalButtonStart} 0%, ${xrystalButtonEnd} 100%)`};

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, ${xrystalButtonStart}dd 0%, ${xrystalButtonEnd}dd 100%);
    box-shadow: 0 8px 25px ${xrystalViolet}50;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const XrystalSignInPage = () => {
  const [emailOrID, setEmailOrID] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const redirectParam = new URLSearchParams(window.location.search).get('redirect');

  const handleLogin = async (e: any = null) => {
    e?.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await postWithCSRFToken('/api/v3/sessions', {
        email: emailOrID,
        password,
      });
      document.location.href = redirectParam ?? '/app/nav/v4/dashboard';
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid login credentials. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const XrystalButton = ({
    children,
    pending,
    onClick,
  }: {
    children: React.ReactNode;
    pending: boolean;
    onClick: () => void;
  }) => (
    <StyledXrystalButton type="submit" onClick={onClick} disabled={pending} pending={pending}>
      {pending ? (
        <div className="flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
          Signing In...
        </div>
      ) : (
        children
      )}
    </StyledXrystalButton>
  );

  return (
    <>
      {isGovcloud() && (
        <div
          className="p-6 mb-6 rounded-md shadow-md relative"
          style={{
            backgroundColor: white,
            border: `2px solid ${xrystalBrightAzure}40`,
          }}
        >
          <div className="text-lg" style={{ color: xrystalDarkNavy }}>
            You are accessing a restricted U.S. Government information system. All system usage may be monitored,
            recorded, and subject to audit. Use of this system indicates consent to monitoring and recording. Any
            unauthorized use of this system is prohibited and subject to criminal and civil penalties.
          </div>
          <div
            className="absolute bottom-0 left-0 right-0 h-2 rounded-b-md"
            style={{
              background: `linear-gradient(90deg, ${xrystalBrightAzure} 0%, ${xrystalViolet} 100%)`,
            }}
          />
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: xrystalDeepOcean }}>
          Welcome to Xrystal
        </h1>
        <p className="text-lg" style={{ color: xrystalDarkNavy }}>
          Sign in to see the future of learning
        </p>
      </div>

      <form onSubmit={handleLogin}>
        <div className="mb-6">
          <label className="block font-semibold mb-2" style={{ color: xrystalDarkNavy }}>
            Email Address or ID
          </label>
          <BrightInput
            className="!my-1 !border-2 !rounded-lg focus:!shadow-lg transition-all duration-200"
            style={{
              borderColor: `${xrystalBrightAzure}60`,
              backgroundColor: white,
            }}
            placeholder="Enter your email or ID"
            type="text"
            value={emailOrID}
            onChange={(e) => setEmailOrID(e.target.value)}
            required
          />
        </div>

        <div className="mb-6">
          <label className="block font-semibold mb-2" style={{ color: xrystalDarkNavy }}>
            Password
          </label>
          <BrightInput
            className="!my-1 !border-2 !rounded-lg focus:!shadow-lg transition-all duration-200"
            style={{
              borderColor: `${xrystalBrightAzure}60`,
              backgroundColor: white,
            }}
            placeholder="Enter your password"
            type="password"
            value={password}
            required
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && (
          <div className="mb-4">
            <ErrorMessage>{error}</ErrorMessage>
          </div>
        )}

        <div className="mb-6">
          <StandardLink to="/signin/forgot-password?brand=xrystal" className="hover:opacity-80 transition-opacity">
            <span
              style={{
                color: xrystalViolet,
                textDecoration: 'none',
              }}
            >
              Forgot password?
            </span>
          </StandardLink>
        </div>

        <XrystalButton pending={isSubmitting} onClick={() => handleLogin()}>
          Sign In to Xrystal
        </XrystalButton>
      </form>

      {/* Crystalline decorative elements */}
      <div className="mt-8 flex justify-center">
        <div
          className="w-16 h-1 rounded-full"
          style={{
            background: `linear-gradient(90deg, transparent 0%, ${xrystalBrightAzure} 50%, transparent 100%)`,
          }}
        />
      </div>
    </>
  );
};
